import { useEffect } from "react";
import Lenis from "lenis";

const clamp01 = (value) => Math.max(0, Math.min(1, value));

export function usePageScroll(pageRef) {
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return;

    const content = page.querySelector(".lg-page-content") || page;
    const reduceQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const coarseQuery = window.matchMedia("(pointer: coarse)");
    const desktopQuery = window.matchMedia("(min-width: 925px)");

    const applyScenes = () => {
      const viewH = page.clientHeight || window.innerHeight;
      const y = page.scrollTop;
      page.style.setProperty("--win-height", `${viewH}px`);

      page.querySelectorAll("[data-scene]").forEach((el) => {
        const top = el.offsetTop;
        const h = el.offsetHeight;
        const pin = Math.max(1, h - viewH);
        const progress = clamp01((y - top) / pin);
        el.style.setProperty("--progress", progress.toFixed(4));

        const relTop = top - y;
        const relBottom = relTop + h;
        const isIn = relTop < viewH * 0.84;
        const isOut = relBottom < viewH * 0.16;
        el.classList.toggle("is-in", isIn && !isOut);
        el.classList.toggle("is-visible", relBottom > 0 && relTop < viewH);
        el.classList.toggle("is-out", isOut);
      });

      page.querySelectorAll("[data-reveal]").forEach((el) => {
        if (el.classList.contains("is-visible")) return;
        if (el.offsetTop - y < viewH * 0.9) {
          el.classList.add("is-visible");
        }
      });
    };

    let lenis = null;
    let raf = 0;
    let nativeScroll = false;

    const enableNative = () => {
      nativeScroll = true;
      page.addEventListener("scroll", applyScenes, { passive: true });
      applyScenes();
    };

    const disableNative = () => {
      if (!nativeScroll) return;
      page.removeEventListener("scroll", applyScenes);
      nativeScroll = false;
    };

    const startLenis = () => {
      disableNative();
      lenis?.destroy();
      lenis = new Lenis({
        wrapper: page,
        content,
        eventsTarget: page,
        lerp: 0.1,
        wheelMultiplier: 1.15,
        smoothWheel: true,
        syncTouch: false,
        autoResize: true,
        overscroll: false,
        prevent: (node) => Boolean(node.closest?.(".lg-marquee")),
      });
      lenis.on("scroll", applyScenes);

      const loop = (time) => {
        lenis?.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
      applyScenes();
    };

    const stopLenis = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      lenis?.destroy();
      lenis = null;
      enableNative();
    };

    const syncMode = () => {
      const smooth =
        desktopQuery.matches && !reduceQuery.matches && !coarseQuery.matches;
      if (smooth && !lenis) startLenis();
      if (!smooth && lenis) stopLenis();
      if (!smooth && !nativeScroll) enableNative();
    };

    const introTimer = window.setTimeout(() => {
      page.classList.add("is-intro-complete");
    }, 80);

    syncMode();
    desktopQuery.addEventListener?.("change", syncMode);
    reduceQuery.addEventListener?.("change", syncMode);

    window.addEventListener("resize", applyScenes);

    return () => {
      window.clearTimeout(introTimer);
      if (raf) cancelAnimationFrame(raf);
      lenis?.destroy();
      disableNative();
      desktopQuery.removeEventListener?.("change", syncMode);
      reduceQuery.removeEventListener?.("change", syncMode);
      window.removeEventListener("resize", applyScenes);
    };
  }, [pageRef]);
}
